import { useViewport } from '@nuvolo/nuux-theme/hooks/useViewport';
import React, { useMemo } from 'react';
import If from '#/common/components/If';
import WorkOrderDataGrid from '../WorkOrderDataGrid';
import WorkOrderList from '../WorkOrderList';
import type { Filter } from '#/common/components/DataGrid';

interface WorkOrdersProps {
  omitColumns?: string[];
  hideNewAction?: boolean;
  permanentFilters?: Filter[];
  hideBulkActions?: boolean;
  toolbarPrefix?: React.ReactNode;
  visibleColumns?: string[];
  gridId?: string;
  showOnlyInternal?: boolean;
}

const WorkOrders: React.FC<WorkOrdersProps> = ({
  omitColumns,
  hideNewAction,
  permanentFilters,
  hideBulkActions,
  toolbarPrefix,
  visibleColumns,
  gridId,
  showOnlyInternal = false,
}) => {
  const { isDesktop } = useViewport();

  // If showOnlyInternal is true, add a filter to exclude FSM-sourced Work Orders.
  // Note: CSV-imported Work Orders (thirdPartySource = 'csv') are still considered
  // "internal" here and remain visible - only FSM bulk-synced data is excluded,
  // since that data set has its own dedicated "FSM Work Orders" tab.
  const effectiveFilters = useMemo(() => {
    if (!showOnlyInternal) {
      return permanentFilters;
    }
    const internalFilter: Filter = ['thirdPartySource', '<>', 'FSM'];
    return permanentFilters ? [...permanentFilters, 'and', internalFilter] : [internalFilter];
  }, [permanentFilters, showOnlyInternal]);

  return (
    <>
      <If condition={isDesktop}>
        <WorkOrderDataGrid
          omitColumns={omitColumns}
          hideNewAction={hideNewAction}
          hideBulkActions={hideBulkActions}
          toolbarPrefix={toolbarPrefix}
          visibleColumns={visibleColumns}
          gridId={gridId}
          permanentFilters={effectiveFilters}
        />
      </If>
      <If condition={!isDesktop}>
        <WorkOrderList hideNewAction={hideNewAction} permanentFilters={effectiveFilters} />
      </If>
    </>
  );
};

export default React.memo(WorkOrders);
